import { useState, useEffect, useRef } from "react";
import { searchService } from "../../services/api";
import CustomDropdown from '../common/CustomDropdown';
import MobileDropdown from './MobileDropdown';
import MobileCountryDropdown from './MobileCountryDropdown';
import { useMediaQuery } from "../../hooks/useMediaQuery";

const SearchFilters = ({ type, filters, onFilterChange }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [allCountries, setAllCountries] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredDivisionCountries, setFilteredDivisionCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [divisionCountrySearch, setDivisionCountrySearch] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isDivisionCountryDropdownOpen, setIsDivisionCountryDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const divisionDropdownRef = useRef(null);

  // Leagues/Competitions state
  const [allLeagues, setAllLeagues] = useState([]);
  
  // Sort options - to be implemented in future

  // Function to organize positions into groups
  const organizePositions = (positions) => {
    if (!Array.isArray(positions)) return [];

    // Normalize structure to ensure fields id, name, parent_id are present
    const normalized = positions.map((p) => ({
      id: p.id ?? p.value ?? p.code ?? p.label, // fallback structure
      name: p.name ?? p.label ?? p.value,
      parent_id: p.parent_id ?? null,
    }));

    const mainPositions = normalized.filter((p) => !p.parent_id);
    const subPositions = normalized.filter((p) => p.parent_id);

    return mainPositions
      .map((main) => ({
        ...main,
        subPositions: subPositions.filter((sub) => sub.parent_id === main.id),
      }))
      .filter(
        (pos) =>
          ![
            "Coach",
            "Assistant Coach",
            "Goalkeeping Coach",
            "Forward Coach",
            "Caretaker Manager",
          ].includes(pos.name)
      );
  };

  // Fetch all countries and positions on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [countriesRes, positionsRes] = await Promise.all([
          searchService.getCountries(),
          searchService.getPositions(),
        ]);

        if (countriesRes.success) {
          setAllCountries(countriesRes.data);
          setFilteredCountries(countriesRes.data);
        }

        if (positionsRes.success) {
          // Positions lookup can either return array or object with positions key
          const positionsArray = Array.isArray(positionsRes.data)
            ? positionsRes.data
            : positionsRes.data.positions || [];
          setAllPositions(positionsArray);
        }

        // Don't fetch leagues initially - they'll be fetched when a country is selected
        // Initialize with empty array
        setAllLeagues([]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
      setIsLoading(false);
    };

    fetchInitialData();
  }, []);

  // Filter countries locally based on search input
  useEffect(() => {
    if (countrySearch.trim()) {
      const filtered = allCountries.filter((country) =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(allCountries);
    }
  }, [countrySearch, allCountries]);

  // Filter division countries based on search input
  useEffect(() => {
    if (divisionCountrySearch.trim()) {
      const filtered = allCountries.filter((country) =>
        country.name.toLowerCase().includes(divisionCountrySearch.toLowerCase())
      );
      setFilteredDivisionCountries(filtered);
    } else {
      setFilteredDivisionCountries(allCountries);
    }
  }, [divisionCountrySearch, allCountries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCountryDropdownOpen(false);
      }
      if (divisionDropdownRef.current && !divisionDropdownRef.current.contains(event.target)) {
        setIsDivisionCountryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Whenever selected country filter changes, fetch leagues specific to that country
  useEffect(() => {
    const fetchCountryLeagues = async () => {
      if (!filters.divisionCountry) {
        setAllLeagues([]);
        return;
      }
      
      try {
        // filters.divisionCountry now contains alpha3code directly
        const leaguesRes = await searchService.getLeagues(filters.divisionCountry);
        console.log(leaguesRes,'this is leaguesRes')
        if (leaguesRes.success) {
          const leaguesArray = Array.isArray(leaguesRes.data)
            ? leaguesRes.data
            : leaguesRes.data.results || [];
          setAllLeagues(leaguesArray);
        }
      } catch (err) {
        console.error("Error fetching leagues for country:", err);
      }
    };

    fetchCountryLeagues();
  }, [filters.divisionCountry]);

  const renderCountryDropdown = (placeholder = "Player Nationality") => {
    if (isMobile) {
      return (
        <MobileCountryDropdown
          value={filters.country || ""}
          onChange={(value) => onFilterChange("country", value)}
          options={filteredCountries}
          placeholder={placeholder}
          searchValue={countrySearch}
          onSearchChange={setCountrySearch}
          isOpen={isCountryDropdownOpen}
          setIsOpen={setIsCountryDropdownOpen}
        />
      );
    }

    return (
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            className="w-48 px-3 py-2 border border-[#D2D4D8] rounded-lg bg-white text-sm placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder={placeholder}
            value={countrySearch}
            onChange={(e) => {
              setCountrySearch(e.target.value);
              setIsCountryDropdownOpen(true);
            }}
            onClick={() => setIsCountryDropdownOpen(true)}
          />
          {filters.country && (
            <button
              type="button"
              className="absolute right-8 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
              onClick={() => {
                onFilterChange("country", "");
                setCountrySearch("");
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="#1E1E1ECC"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
            <button
              type="button"
              className="p-1"
              onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            >
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="#1E1E1ECC  "
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>

        {isCountryDropdownOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {isLoading ? (
              <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
            ) : filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.id}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      // For new API, use alpha3code as the value
                      onFilterChange("country", country.alpha3code || country.id);
                      setCountrySearch(country.name);
                      setIsCountryDropdownOpen(false);
                    }}
                >
                  {/* Render via the already-loaded flag-icons CSS class
                      instead of a per-flag <img> request — matches the
                      Tender / Discovery / Transfer News country
                      dropdowns. */}
                  {country.country_code || country.alpha2code ? (
                    <span
                      className={`fi fi-${(
                        country.country_code || country.alpha2code
                      ).toLowerCase()} flex-shrink-0`}
                      style={{ width: "1.333em", height: "1em" }}
                      role="img"
                      aria-label={country.name}
                    />
                  ) : (
                    country.image_url && (
                      <img
                        src={country.image_url}
                        alt={country.name}
                        className="w-5 h-4 object-cover"
                      />
                    )
                  )}
                  <span>{country.name}</span>
                  {country.fifa_name && (
                    <span className="text-xs text-gray-500">
                      ({country.fifa_name})
                    </span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No countries found
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDivisionCountryDropdown = () => {
    if (isMobile) {
      return (
        <MobileCountryDropdown
          value={filters.divisionCountry || ""}
          onChange={(value) => onFilterChange("divisionCountry", value)}
          options={filteredDivisionCountries}
          placeholder="Division Country"
          searchValue={divisionCountrySearch}
          onSearchChange={setDivisionCountrySearch}
          isOpen={isDivisionCountryDropdownOpen}
          setIsOpen={setIsDivisionCountryDropdownOpen}
        />
      );
    }

    return (
      <div className="relative" ref={divisionDropdownRef}>
        <div className="relative">
          <input
            type="text"
            className="w-48 px-3 py-2 border border-[#D2D4D8] rounded-lg bg-white text-sm placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Division Country"
            value={divisionCountrySearch}
            onChange={(e) => {
              setDivisionCountrySearch(e.target.value);
              setIsDivisionCountryDropdownOpen(true);
            }}
            onClick={() => setIsDivisionCountryDropdownOpen(true)}
          />
          {filters.divisionCountry && (
            <button
              type="button"
              className="absolute right-8 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
              onClick={() => {
                onFilterChange("divisionCountry", "");
                setDivisionCountrySearch("");
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="#1E1E1ECC"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
            <button
              type="button"
              className="p-1"
              onClick={() => setIsDivisionCountryDropdownOpen(!isDivisionCountryDropdownOpen)}
            >
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="#1E1E1ECC  "
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>

        {isDivisionCountryDropdownOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {isLoading ? (
              <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
            ) : filteredDivisionCountries.length > 0 ? (
              filteredDivisionCountries.map((country) => (
                <button
                  key={country.id}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      // For new API, use alpha3code as the value
                      onFilterChange("divisionCountry", country.alpha3code || country.id);
                      setDivisionCountrySearch(country.name);
                      setIsDivisionCountryDropdownOpen(false);
                    }}
                >
                  {/* Render via the already-loaded flag-icons CSS class
                      instead of a per-flag <img> request — matches the
                      Tender / Discovery / Transfer News country
                      dropdowns. */}
                  {country.country_code || country.alpha2code ? (
                    <span
                      className={`fi fi-${(
                        country.country_code || country.alpha2code
                      ).toLowerCase()} flex-shrink-0`}
                      style={{ width: "1.333em", height: "1em" }}
                      role="img"
                      aria-label={country.name}
                    />
                  ) : (
                    country.image_url && (
                      <img
                        src={country.image_url}
                        alt={country.name}
                        className="w-5 h-4 object-cover"
                      />
                    )
                  )}
                  <span>{country.name}</span>
                  {country.fifa_name && (
                    <span className="text-xs text-gray-500">
                      ({country.fifa_name})
                    </span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No countries found
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // min-w-0 px-3 py-2

  const renderFilterDropdown = (key, options, label, width = "w-40", disabled = false, noOptionsText = "No options found") => {
    if (isMobile) {
      return (
        <MobileDropdown
          value={filters[key] || ""}
          onChange={(value) => onFilterChange(key, value)}
          options={options}
          placeholder={label}
          displayProperty={options[0]?.label ? "label" : "name"}
          valueProperty={options[0]?.value ? "value" : "id"}
          disabled={disabled}
          noOptionsText={noOptionsText}
        />
      );
    }

    return (
      <div className="relative">
        <div className="relative">
          <CustomDropdown
            options={options}
            value={filters[key] || ""}
            onChange={(value) => onFilterChange(key, value)}
            placeholder={label}
            displayProperty={options[0]?.label ? "label" : "name"}
            valueProperty={options[0]?.value ? "value" : "id"}
            className={`${width} text-sm px-3 pr-8 border border-[#D2D4D8] rounded-lg bg-white placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500 py-2 ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={disabled}
            noOptionsText={noOptionsText}
          />
          {filters[key] && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
              onClick={() => onFilterChange(key, "")}
              disabled={disabled}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPositionDropdown = () => {
    const organizedPositions = organizePositions(allPositions);
    const positionOptions = organizedPositions.flatMap(mainPosition => [
      { id: mainPosition.id, name: mainPosition.name },
      ...(mainPosition.subPositions?.map(subPosition => ({ 
        id: subPosition.id, 
        name: `${mainPosition.name} - ${subPosition.name}` 
      })) || [])
    ]);

    if (isMobile) {
      return (
        <MobileDropdown
          value={filters.position || ""}
          onChange={(value) => onFilterChange("position", value)}
          options={positionOptions}
          placeholder="Position"
          displayProperty="name"
          valueProperty="id"
        />
      );
    }

    return (
      <div className="relative">
        <div className="relative">
          <CustomDropdown
            options={positionOptions}
            value={filters.position || ""}
            onChange={(value) => onFilterChange("position", value)}
            placeholder="Position"
            displayProperty="name"
            valueProperty="id"
            className="w-40 min-w-0 text-sm px-3 pr-8 py-2 border border-[#D2D4D8] rounded-lg bg-white placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {filters.position && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
              onClick={() => onFilterChange("position", "")}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };


  const getFiltersForType = () => {
    const renderFiltersByType = () => {
      switch (type) {
        case 'players':
          return (
            <>
              {renderCountryDropdown()}
              {renderDivisionCountryDropdown()}
              {renderFilterDropdown('level', [{ id: 'pro', name: 'Professional' }, { id: 'youth', name: 'Youth' }], 'Level', 'w-52')}
              {renderFilterDropdown('competition_id', allLeagues.map(l => ({ id: l.id, name: l.name })), 'Division', 'w-48', false, filters.divisionCountry ? 'No options found' : 'Select division country first')}
              {renderPositionDropdown()}
            </>
          );
        
        case 'clubs':
          return (
            <>
              {renderCountryDropdown("Country")}
            </>
          );
        
        case 'coaches':
          return null;
        
        default:
          return null;
      }
    };

    // Desktop view - unchanged
    const renderDesktopFilters = () => {
      const filters = renderFiltersByType();
      // If no filters, return null
      if (!filters) {
        return null;
      }
      return (
        <div className="flex items-center gap-4 w-full">
          <div className="flex items-center gap-4 flex-1">
            {filters}
          </div>
        </div>
      );
    };

    // Mobile view - structured in rows as per requirements
    const renderMobileFilters = () => {
      // Get all filters based on type
      const allFilters = (() => {
        switch (type) {
          case 'players':
            return [
              renderCountryDropdown(),
              renderDivisionCountryDropdown(),
              renderFilterDropdown('level', [{ id: 'pro', name: 'Professional' }, { id: 'youth', name: 'Youth' }], 'Level'),
              renderFilterDropdown('competition_id', allLeagues.map(l => ({ id: l.id, name: l.name })), 'Division', 'w-full', false, filters.divisionCountry ? 'No options found' : 'Select division country first'),
              renderPositionDropdown()
            ];
          case 'clubs':
            return [
              renderCountryDropdown("Country")
            ];
          case 'coaches':
            return [];
          default:
            return [];
        }
      })();

      // If no filters, return null
      if (allFilters.length === 0) {
        return null;
      }

      // Group filters into rows (3 per row)
      const firstRowFilters = allFilters.slice(0, 3);
      const secondRowFilters = allFilters.slice(3);

      return (
        <div className="flex flex-col w-full">
          {/* First row of filters - up to 3 */}
          {firstRowFilters.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {firstRowFilters.map((filter, index) => (
                <div key={index} className="text-xs">{filter}</div>
              ))}
            </div>
          )}

          {/* Second row of filters - any remaining */}
          {secondRowFilters.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {secondRowFilters.map((filter, index) => (
                <div key={index} className="text-xs">{filter}</div>
              ))}
            </div>
          )}

        </div>
      );
    };

    return isMobile ? renderMobileFilters() : renderDesktopFilters();
  };

  return getFiltersForType();
};

export default SearchFilters;
